import React from 'react'
import type { RatingPolarity } from '@digital-twins/shared-types'

import { TextClamp } from '../../components/text/TextClamp'
import { FastTooltip } from '../_shared/FastTooltip'
import { roundHalfAwayFromZero } from '../../foundations/producer-rounding'
import { RatingBand, RatingBarPlan } from './bar-plan'
import { splitRatingBuckets } from './legacy/rating-likert-split'
import { divergingLikertScaledFill, divergingLikertScaledText } from './piece-fill'
import { divergingLikertPieceIntensities } from './stat-view-plan'

/**
 * One diverging bar row of the rating-likert slide (W7 D5): the name column
 * (with its still), the bar drawn from a `RatingBarPlan` and the net column.
 *
 * Moved here from `slides/RatingLikertSlide.tsx` unchanged, so the row lives
 * with the diverging-likert plot it draws; the slide imports it from here and
 * keeps its frame. Geometry is the slide's (Stage 1 freezes it): every
 * constant below is the one the slide drew with. The row name is drawn by the
 * host (`renderLabel`, as the quadrant's `renderPointLabel`): plot code never
 * imports a slide, so the deck passes its editable text and any other host
 * gets a clamped name.
 */

const TEXT = 'var(--slide-cover-bg)'
const MINT = 'var(--slide-diverging-positive)'
const RED = 'var(--slide-diverging-negative)'

/**
 * Name column on a labelled row. 220px holds a banner still and the asset name
 * on its own line; the old 132px column cut the name after the shared prefix.
 */
const LABEL_COLUMN_PX = 220
const COLUMN_GAP_PX = 40
/**
 * The widest the name column of a static stack grows (`staticStackColumns`): twice
 * the interactive column, so a long split name ("... (n = 27)") wraps whole in a
 * few lines and the bar keeps well over half the row. A judgement, not a derivation.
 */
const STATIC_STACK_LABEL_COLUMN_MAX_PX = 2 * LABEL_COLUMN_PX
/**
 * Kept for the row-width contract with the chart-segment gear. The bar itself
 * fills the row; these no longer size the track.
 */
const UNLABELLED_BAR_WIDTH = 872
const LABELLED_BAR_WIDTH = 132 + COLUMN_GAP_PX + UNLABELLED_BAR_WIDTH - LABEL_COLUMN_PX - COLUMN_GAP_PX
/** Readable in-bar caption on the compact 24px track (18px bar). Never drop to 9px. */
const SEGMENT_CAPTION_FONT_PX = 12
const SEGMENT_CAPTION_COMPACT_FONT_PX = 11

/**
 * Width of one diverging bar row: label column + bar + score column.
 *
 * It is the panel's width minus the breakdown gear's clearance, which is what lets the
 * row survive unchanged when a segment is drawn inside the tabs column — and also why
 * that figure has to opt out of the tabs column's left gutter. The relationship is
 * asserted in ChartSegmentLayout.test.tsx rather than left as a coincidence between
 * two files.
 */
export const RATING_LIKERT_BAR_ROW_WIDTH_PX = 1256

/**
 * One precision rule for every percentage this slide prints: whole numbers.
 *
 * The old rule was `Number.isInteger(value) ? 0 : 1` decimal places, which is not a
 * rule at all but a coincidence test: a share that happens to land on a whole number
 * printed one way and its neighbour printed another. The KPI tile beside the bar
 * prints `rating_kpi_value`, which the producer has already rounded to a whole
 * number, so the bar captions had a second spelling of the same statistic on the
 * same slide. Every NPS slide took the decimal branch, because a promoter share out
 * of an arbitrary base is almost never an integer.
 *
 * Rounding goes through `roundHalfAwayFromZero`, never `Math.round` and never `toFixed(0)`:
 * both of those are half-UP and would print 63 where the producer's Python `round`
 * printed 62. Precision is not raised anywhere by this: the headline was already
 * whole and the rest now conforms to it.
 */
function fmtPercent(value: number) {
  return `${roundHalfAwayFromZero(value)}%`
}

function signedScore(value: number) {
  const rounded = roundHalfAwayFromZero(value)
  return rounded > 0 ? `+${rounded}` : String(rounded)
}

/** Arrow plus the magnitude. The localized word stays on the accessible name. */
function netGlyph(value: number): string {
  const score = signedScore(value)
  if (score.startsWith('+')) {
    return `↑ ${score}`
  }
  if (score.startsWith('-')) {
    return `↓ ${score.slice(1)}`
  }
  return score
}

interface RatingSegmentProps {
  /** Segment index along the bar, low end of the scale first. */
  id: string
  band: RatingBand
  label: string
  /** The printed share. */
  value: number
  /** The share the segment is drawn with (the bar plan's unrounded `share`). */
  share: number
  color: string
  textColor: string
  height?: number
  /** Explicit interactive render. Omitted and static keep the native title. */
  interactive?: boolean
}

/**
 * One segment of the bar, exactly as the builder stamped it.
 *
 * It prints its own share and nothing else. The old bar printed a caption that
 * had been recomputed from the measured groups in three separate places, because
 * its two soft bands each carried half the neutral bucket and could not honestly
 * name what they were drawn with. A middle band is now one segment of one colour
 * that reports its share once, so there is nothing left to reconcile.
 */
function RatingSegment(props: RatingSegmentProps) {
  const height = props.height ?? 90

  if (props.share <= 0) {
    return null
  }

  const title = `${props.label}: ${fmtPercent(props.value)}`
  const segment = (
    <div
      data-testid="rating-likert-segment"
      data-segment={props.id}
      data-band={props.band}
      title={props.interactive ? undefined : title}
      style={{
        width: `${props.share}%`,
        flexShrink: 0,
        height,
        background: props.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <TextClamp
        data-testid="rating-likert-segment-caption"
        minLines={1}
        maxLines={1}
        lineHeight="1.2"
        style={{
          fontSize: height >= 28 ? SEGMENT_CAPTION_FONT_PX : SEGMENT_CAPTION_COMPACT_FONT_PX,
          fontWeight: 600,
          color: props.textColor,
          textAlign: 'center',
          width: '100%',
        }}
      >
        {fmtPercent(props.value)}
      </TextClamp>
    </div>
  )
  if (!props.interactive) {
    return segment
  }
  return <FastTooltip label={title}>{segment}</FastTooltip>
}

/** What the host draws the row name from (`RatingLikertBarRowProps.renderLabel`). */
export interface RatingLikertBarRowLabel {
  value: string
  /** The edit field (`rows.{i}.label` unless the host names another). */
  field: string
  rowIndex: number
  editable?: boolean
  onEdit?: (field: string, value: string) => void
  renderMode?: 'interactive' | 'static'
  maxLines: number
  style: React.CSSProperties
}

/** Without a host renderer: the name clamped to its lines, the whole name on hover. */
function defaultRowLabel(label: RatingLikertBarRowLabel): React.ReactNode {
  if (label.renderMode === 'interactive') {
    return (
      <FastTooltip label={label.value}>
        <span style={{ minWidth: 0, width: '100%' }}>
          <TextClamp minLines={1} maxLines={label.maxLines} lineHeight="1.2" style={label.style}>
            {label.value}
          </TextClamp>
        </span>
      </FastTooltip>
    )
  }
  return (
    <TextClamp title={label.value} minLines={1} maxLines={label.maxLines} lineHeight="1.2" style={label.style}>
      {label.value}
    </TextClamp>
  )
}

export interface RatingLikertBarRowProps {
  dataStatus?: ReturnType<typeof splitRatingBuckets>['dataStatus']
  /**
   * The row's own name, when it has one.
   *
   * A comparison stack labels each row because the label is what one row is being read
   * AGAINST. A lone bar has no such neighbour, and the label it used to print was the
   * generic `displayLabels.rowLabel` ("Rating") -- a word the slide's headline, eyebrow
   * and chart title all already say -- while burning the name column plus its gap on
   * every single-row slide. Omit it and the bar starts at the panel inset.
   */
  label?: string | null
  /** Still for this row. Absent, and the name column stays text-only. */
  imageUrl?: string | null
  plan: RatingBarPlan
  /** Printed only beside a comparison row; a lone bar has nothing to compare to. */
  netLabel?: string | null
  /** Printed in the net column when the row has no figure, in the deck language (`displayLabels.notAvailable`). */
  notAvailableLabel?: string | null
  barHeight?: number
  trackHeight?: number
  testId?: string
  rowIndex?: number
  labelField?: string
  editable?: boolean
  onEdit?: (field: string, value: string) => void
  /** Plots plan LB: a static render (PDF) keeps the ellipsis and draws no (i). */
  renderMode?: 'interactive' | 'static'
  /** Draws the row name (the deck's editable slide text); a clamped name when absent. */
  renderLabel?: (label: RatingLikertBarRowLabel) => React.ReactNode
  /**
   * Wave D (static renders draw complete text whole): the row sits in a host stack
   * laid out by `staticStackColumns` / `staticStackRows`, as a subgrid of it. Every
   * name of the stack shares one column that widens for long names, and the row
   * grows to the height its name needs while the stack has room. Without it the row
   * is its own fixed grid.
   */
  inStaticStack?: boolean
  /**
   * Which point of the scale is the good outcome. A point distribution steps
   * its colour the way the survey plot does; omitted means higher is better.
   */
  polarity?: RatingPolarity
}

/** One 16px name line at the row's 1.2 line height. */
const NAME_LINE_PX = 16 * 1.2
/** The name never takes more than this many lines, however tall the track. */
const NAME_MAX_LINES = 4

/**
 * Plots plan LB (owner decision 24): the lines a text-only row name may take.
 * The name is centred in its row's track, so every whole line the track holds
 * is room no other box uses: a longer name prints whole instead of ending in
 * an ellipsis, and nothing else on the slide moves.
 */
export function likertNameMaxLines(trackHeight: number): number {
  return Math.max(1, Math.min(NAME_MAX_LINES, Math.floor(trackHeight / NAME_LINE_PX)))
}

/**
 * Banner still height for a stacked name cell.
 *
 * A 3-row chart's track is 72px. A 40px still plus two lines of type does not fit
 * there, so the still shrinks and the name keeps the full column. Tracks under
 * 40px have room for the name only.
 */
function stillHeightForTrack(trackHeight: number): number {
  if (trackHeight >= 100) {
    return 40
  }
  if (trackHeight >= 68) {
    return 24
  }
  if (trackHeight >= 40) {
    return 16
  }
  return 0
}

/**
 * The name cell stacks the still above the name, so on an interactive surface
 * the name and its (i) share a row. A static render keeps the name alone, as
 * before.
 */
function nameInRow(name: React.ReactNode, renderMode?: 'interactive' | 'static') {
  if (renderMode === 'static') {
    return name
  }
  return <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', minWidth: 0 }}>{name}</div>
}

/**
 * The column template of a static stack of rows (`inStaticStack`). The name column
 * is as wide as its longest name needs, from the interactive 220px up to
 * `STATIC_STACK_LABEL_COLUMN_MAX_PX`, where the names wrap.
 */
export function staticStackColumns(): string {
  return `fit-content(${STATIC_STACK_LABEL_COLUMN_MAX_PX}px) minmax(0, 1fr) auto`
}

/**
 * Each row of a static stack starts at its track height and grows to what its name
 * needs, out of the room the stack has left (the grid shares that room among the
 * rows that want it). The tracks always fit the clip, so a row never pushes another
 * row's bar out of the panel; only a name with no room left is clipped in its row.
 */
export function staticStackRows(trackHeight: number): string {
  return `minmax(${trackHeight}px, max-content)`
}

export function RatingLikertBarRow(props: RatingLikertBarRowProps) {
  const { label, plan } = props
  const imageUrl = (props.imageUrl || '').trim()
  const stacked = Boolean(props.inStaticStack)
  // A stacked row always draws its name cell, so its bar stays in the stack's bar column.
  const labelled = Boolean(label) || stacked
  const barHeight = props.barHeight ?? 90
  const trackHeight = props.trackHeight ?? 113
  const compact = trackHeight < 40
  const rowIndex = props.rowIndex ?? 0
  const stillHeight = imageUrl ? stillHeightForTrack(trackHeight) : 0
  const showStill = stillHeight > 0
  const nameMaxLines = showStill
    ? (trackHeight - stillHeight - 4 >= 38 ? 2 : 1)
    : likertNameMaxLines(trackHeight)
  const barTop = Math.max(0, Math.round((trackHeight - barHeight) / 2))
  // A bar whose facts are missing draws nothing at all, so the column has to SAY
  // so: an empty bar beside an empty column reads as a zero that was measured.
  // That notice is not a net, and it is the one thing a single bar still prints
  // here.
  const unavailable = Boolean(props.dataStatus && props.dataStatus !== 'available')
  const showNet = unavailable || Boolean(props.netLabel)
  const intensities = divergingLikertPieceIntensities(
    plan.pieces.map((piece) => ({ tone: piece.band })),
    props.polarity ?? 'higher_is_better',
  )
  // legacy reader only: a deck built since AI part 3 stamps `displayLabels.notAvailable`.
  const notAvailable = props.notAvailableLabel ?? 'N/A'

  return (
    <div
      data-testid={props.testId}
      style={{
        display: 'grid',
        alignItems: 'center',
        ...(stacked
          ? { gridTemplateColumns: 'subgrid', gridColumn: '1 / -1' }
          : {
              gridTemplateColumns: labelled
                ? `${LABEL_COLUMN_PX}px minmax(0, 1fr) auto`
                : 'minmax(0, 1fr) auto',
              columnGap: COLUMN_GAP_PX,
              width: RATING_LIKERT_BAR_ROW_WIDTH_PX,
              height: trackHeight,
            }),
      }}
    >
      {labelled ? (
        <div
          data-testid={`rating-likert-label-${rowIndex}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: showStill ? 4 : 0,
            // A stacked name sets the stack's column, at least the interactive width.
            minWidth: stacked ? LABEL_COLUMN_PX : 0,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {showStill ? (
            props.renderMode === 'interactive' ? (
              <FastTooltip label={label as string}>
                <img
                  src={imageUrl}
                  alt=""
                  data-testid={`rating-likert-thumb-${rowIndex}`}
                  style={{
                    width: '100%',
                    height: stillHeight,
                    objectFit: 'contain',
                    objectPosition: 'left center',
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                />
              </FastTooltip>
            ) : (
              <img
                src={imageUrl}
                alt=""
                title={label as string}
                data-testid={`rating-likert-thumb-${rowIndex}`}
                style={{
                  width: '100%',
                  height: stillHeight,
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              />
            )
          ) : null}
          {label ? nameInRow(
            (props.renderLabel ?? defaultRowLabel)({
              value: label as string,
              field: props.labelField || `rows.${rowIndex}.label`,
              rowIndex,
              editable: props.editable,
              onEdit: props.onEdit,
              renderMode: props.renderMode,
              maxLines: nameMaxLines,
              style: { fontSize: 16, color: TEXT, minWidth: 0, width: '100%' },
            }),
            props.renderMode,
          ) : null}
        </div>
      ) : null}
      <div
        style={{
          position: 'relative',
          width: '100%',
          minWidth: 0,
          // A stacked row is as tall as its grid row, which its name may grow.
          ...(stacked ? { alignSelf: 'stretch', minHeight: barHeight } : { height: trackHeight }),
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: stacked ? `calc(50% - ${barHeight / 2}px)` : barTop,
            width: '100%',
            height: barHeight,
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            background: 'transparent',
          }}
        >
          {plan.pieces.map((piece, index) => {
            const intensity = intensities[index] ?? 1
            const fill = divergingLikertScaledFill(piece.tone.color, intensity)
            return (
              <RatingSegment
                key={`${piece.index}-${piece.label}`}
                id={String(piece.index)}
                band={piece.band}
                label={piece.label}
                value={piece.value}
                share={piece.share}
                color={fill}
                textColor={divergingLikertScaledText(piece.band, intensity, fill)}
                height={barHeight}
                interactive={props.renderMode === 'interactive'}
              />
            )
          })}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 0, display: 'flex',
        flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : 'flex-end',
        justifyContent: 'flex-end', gap: compact ? 8 : 0 }}>
      {showNet ? (
        <span
          data-testid={`rating-likert-score-${rowIndex}`}
          aria-label={unavailable ? notAvailable : `${props.netLabel} ${signedScore(plan.net)}`}
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: plan.net < 0 ? RED : MINT,
            textAlign: 'right',
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {unavailable ? notAvailable : netGlyph(plan.net)}
        </span>
      ) : null}
      </div>
    </div>
  )
}

export {
  COLUMN_GAP_PX as RATING_LIKERT_COLUMN_GAP_PX,
  LABEL_COLUMN_PX as RATING_LIKERT_LABEL_COLUMN_PX,
  LABELLED_BAR_WIDTH as RATING_LIKERT_LABELLED_BAR_WIDTH_PX,
  UNLABELLED_BAR_WIDTH as RATING_LIKERT_UNLABELLED_BAR_WIDTH_PX,
}
